var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useEffect, useRef, useState } from 'react';
import NextLink from 'next/link';
import hive from '@bombas/hive';
import * as Styled from './ImageCarousel.styled';
import InView from '../../../Carousel/InView';
import { Image } from '../ProductCardImage/ProductCardImage.styled';
/**
 * Carousel for multiple images.
 *
 * This component is used in the ProductCard component to display multiple images.
 */
var ImageCarousel = function (_a) {
    var _b;
    var className = _a.className, images = _a.images, onClick = _a.onClick, pdpLink = _a.pdpLink, productTitle = _a.productTitle, productCardPosition = _a.productCardPosition, _c = _a.shouldChangeOnHover, shouldChangeOnHover = _c === void 0 ? false : _c, shopifyId = _a.shopifyId, _d = _a.dataLabel, dataLabel = _d === void 0 ? 'productCard_primary' : _d, _e = _a.isHovered, isHovered = _e === void 0 ? false : _e;
    var carouselRef = useRef(null);
    var _f = useState([0]), currentSlidesInView = _f[0], setSlideInView = _f[1];
    var _g = useState('next'), currentDirection = _g[0], setCurrentDirection = _g[1];
    var _h = useState(true), hasSnappingRule = _h[0], setHasSnappingRule = _h[1];
    var _j = useState(false), hasUserInteracted = _j[0], setHasUserInteracted = _j[1];
    var _k = useState(true), shouldTrackSlideChange = _k[0], setShouldTrackSlideChange = _k[1];
    var _l = useState([]), savedSlidesInView = _l[0], setSavedSlidesInView = _l[1];
    var handleInViewChange = function (slide, isInView) {
        setSlideInView(function (state) {
            var newState;
            if (isInView) {
                newState = Array.from(new Set(__spreadArray(__spreadArray([], state, true), [slide], false))).sort(function (a, b) { return a - b; });
            }
            else {
                newState = state.filter(function (item) { return item !== slide; });
            }
            return newState;
        });
    };
    var handleInfiniteLoop = function (direction) {
        if (!carouselRef.current) {
            return;
        }
        setCurrentDirection(direction);
        setHasSnappingRule(false);
        /**
         * Don't track slide change when infinite loop
         * is scrolling to the first or last slide.
         */
        setShouldTrackSlideChange(false);
        var scrollWidth = carouselRef.current.scrollWidth;
        carouselRef.current.scrollTo({
            left: direction === 'next' ? 0 : scrollWidth,
            behavior: 'smooth'
        });
    };
    /**
     * Change one slide forward or backward
     */
    var slideOnce = function (direction, behavior, 
    /**
     * If index is provided, go directly to that slide.
     */
    index) {
        if (behavior === void 0) { behavior = 'smooth'; }
        if (!carouselRef.current) {
            return;
        }
        var slides = carouselRef.current.children;
        var _a = slides[index !== null && index !== void 0 ? index : 0], offsetLeft = _a.offsetLeft, offsetWidth = _a.offsetWidth;
        var currentScrollPosition = carouselRef.current.scrollLeft;
        var sign = direction === 'next' ? 1 : -1;
        carouselRef.current.scrollTo({
            left: index !== undefined && index !== null
                ? offsetLeft
                : currentScrollPosition + sign * offsetWidth,
            behavior: behavior
        });
    };
    /**
     * Go directly to a specific slide number.
     */
    var slideTo = function (index, behavior) {
        if (behavior === void 0) { behavior = 'instant'; }
        return function () {
            hive.track('PRODUCT_CARD_CAROUSEL_CHANGE', {
                index: index,
                title: productTitle
            });
            slideOnce('next', behavior, index);
            setHasUserInteracted(true);
        };
    };
    var onArrowClick = function (direction) {
        hive.track('PRODUCT_CARD_CAROUSEL_ARROW_CLICK', {
            direction: direction,
            title: productTitle
        });
        slideOnce(direction);
        setCurrentDirection(direction);
        setHasUserInteracted(true);
    };
    var carouselImages = images.map(function (image, index) {
        var carouselImage = (React.createElement(Image, { key: image.src, src: image.src.includes('https://') ? image.src : "https://".concat(image.src), alt: image.alt || productTitle, isLazy: index !== 0, withProgressiveLoading: index !== 0, draggable: "false" }));
        return pdpLink ? (React.createElement(NextLink, { href: pdpLink, onClick: onClick, "aria-label": productTitle, tabIndex: -1, "data-label": dataLabel, draggable: "false" }, carouselImage)) : (carouselImage);
    });
    var trackSlideChange = function (currentSlide) {
        hive.track('PRODUCT_CARD_CAROUSEL_UPDATE', {
            index: currentSlide,
            title: productTitle,
            position: productCardPosition,
            shopifyId: shopifyId
        });
    };
    var handleWheel = function (e) {
        // Detect horizontal scroll
        if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
            setHasUserInteracted(true);
        }
    };
    var onMouseEnter = function () {
        if (shouldChangeOnHover && !hasUserInteracted) {
            /**
             * Automatically show second slide on hover.
             */
            if (savedSlidesInView.length) {
                slideOnce('next', 'instant', savedSlidesInView[0]);
            }
            else if (carouselImages.length > 1) {
                slideOnce('next', 'instant', 1);
            }
        }
    };
    var onMouseLeave = function () {
        if (shouldChangeOnHover) {
            /**
             * Reset to slide one when not hovered.
             * And reset user interaction state.
             */
            slideOnce('next', 'instant', 0);
            if (hasUserInteracted) {
                setSavedSlidesInView(currentSlidesInView[0] === 0 ? [] : currentSlidesInView);
            }
            setHasUserInteracted(false);
        }
    };
    useEffect(function () {
        var carouselElement = carouselRef.current;
        if (carouselElement) {
            carouselElement.addEventListener('wheel', handleWheel);
        }
        return function () {
            if (carouselElement) {
                carouselElement.removeEventListener('wheel', handleWheel);
            }
        };
    }, []);
    useEffect(function () {
        /**
         * Tracking for infinite loop
         */
        var isInfiniteLoopFinished = (currentSlidesInView[0] === 0 && currentDirection === 'next') ||
            (currentSlidesInView[0] === images.length - 1 &&
                currentDirection === 'previous');
        if (isInfiniteLoopFinished) {
            setShouldTrackSlideChange(true);
        }
        if (shouldTrackSlideChange &&
            hasUserInteracted &&
            currentSlidesInView[0] > -1) {
            trackSlideChange(currentSlidesInView[0]);
        }
    }, [currentSlidesInView[0], shouldTrackSlideChange]);
    useEffect(function () {
        if (isHovered) {
            setHasSnappingRule(true);
            if (carouselRef.current) {
                carouselRef.current.focus({
                    preventScroll: true
                });
            }
        }
    }, [isHovered]);
    // if the first image src has changed from the previous value (i.e. variant change),
    // reset the saved slides in view to an empty array
    useEffect(function () {
        var _a, _b;
        if (savedSlidesInView.length &&
            ((_a = images[0]) === null || _a === void 0 ? void 0 : _a.src) !== ((_b = carouselImages[0]) === null || _b === void 0 ? void 0 : _b.props.src)) {
            setSavedSlidesInView([]);
        }
    }, [(_b = images[0]) === null || _b === void 0 ? void 0 : _b.src]);
    return (React.createElement(Styled.ImageCarouselContainer, { className: className },
        React.createElement(Styled.ImageCarousel, { ref: carouselRef, onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave, onTouchStart: function () {
                setHasUserInteracted(true);
                setHasSnappingRule(true);
            }, hasSnappingRule: hasSnappingRule, tabIndex: 0 }, carouselImages.map(function (image, index) { return (React.createElement(Styled.Content, { tabIndex: index !== 0 ? -1 : 0, key: index },
            React.createElement(InView, { parent: carouselRef.current, setElementInView: handleInViewChange, position: index, isLazy: false, threshold: [0.2, 0.8] }, image))); })),
        images.length > 1 && (React.createElement(Styled.SelectCarousel, { totalSlides: images.length, currentSlide: currentSlidesInView[0] ? currentSlidesInView[0] + 1 : 1, onIndicatorClick: slideTo, onLeftArrowClick: function () {
                onArrowClick('previous');
            }, onRightArrowClick: function () {
                onArrowClick('next');
            }, onInfiniteLoop: handleInfiniteLoop }))));
};
export default ImageCarousel;
